import { useState } from 'preact/hooks';
import { SCHOOL_YEAR_END, SCHOOL_YEAR_START } from '../../data/school-year-2026-2027.ts';
import {
  DAYS,
  closureForDate,
  closureForISO,
  schoolDayPositionISO,
  type DayIndex,
} from '../../lib/calendar.ts';
import { addDays, frDayDate, parseLocalDate, toLocalISO } from '../../lib/dates.ts';
import { isLinkedTo } from '../../lib/lesson-links.ts';
import { PERIODS, RITUAL_END, RITUAL_START, RITUAL_TITLE } from '../../lib/slots.ts';
import { RITUAL_COLOR, SUBJECTS, subjectColor, subjectDomains, subjectFamily } from '../../lib/subjects.ts';
import { live, useStore, type LessonInput } from '../../store/store.ts';
import type { SlotIndex } from '../../store/types.ts';
import { closeModal, openModal, toast } from '../../ui/ui-store.ts';
import { CompetenceField } from './CompetenceField.tsx';

interface Props {
  /** Existing lesson id, or null to create one. */
  lessonId: string | null;
  /** Existing lesson whose contents seed a new card. */
  sourceLessonId?: string;
  initialDay: DayIndex;
  initialSlot: SlotIndex;
}

/** "3 observations", "1 observation", "0 observation" — French keeps 0 singular. */
function count(n: number, one: string, many: string): string {
  return `${n} ${n > 1 ? many : one}`;
}

/** The date a copy would land on, in words — or why it cannot land there. */
function targetLabel(iso: string): string {
  const position = schoolDayPositionISO(iso);
  if (!position) return 'Ce n’est pas un jour de classe de l’année scolaire.';

  const label = `${DAYS[position.day]} ${frDayDate(parseLocalDate(iso))}`;
  const closure = closureForISO(iso);
  return closure ? `${label} — ${closure.label}` : label;
}

export function LessonModal({ lessonId, sourceLessonId, initialDay, initialSlot }: Props) {
  const monday = useStore((s) => s.currentMonday)();
  const existing = useStore((s) => (lessonId ? s.lessons.find((l) => l.id === lessonId) : undefined));
  const source = useStore((s) =>
    sourceLessonId ? s.lessons.find((l) => l.id === sourceLessonId) : undefined,
  );
  const seed = existing ?? source;

  // `live()` builds a new array, so it must run outside the selector.
  const observations = live(useStore((s) => s.observations));
  const evaluations = live(useStore((s) => s.evaluations));

  const [day, setDay] = useState<DayIndex>(seed?.day ?? initialDay);
  const [slot, setSlot] = useState<SlotIndex>(seed?.slot ?? initialSlot);
  const [subject, setSubject] = useState(seed?.subject ?? 'Français');
  const [domain, setDomain] = useState(seed?.domain ?? '');
  const [title, setTitle] = useState(
    seed?.title ?? (initialSlot === -1 ? RITUAL_TITLE : ''),
  );
  const [detail, setDetail] = useState(seed?.detail ?? '');
  const [lessonNumber, setLessonNumber] = useState(seed?.lessonNumber ?? '');
  const [competenceIds, setCompetenceIds] = useState<string[]>(seed?.competenceIds ?? []);
  const [material, setMaterial] = useState(seed?.material ?? '');
  const [differentiation, setDifferentiation] = useState(seed?.differentiation ?? '');

  // A copy is dated rather than placed on a weekday, so it can go to any week
  // of the year. A week later by default: the same day would drop it straight
  // onto its original's cell.
  const [targetDate, setTargetDate] = useState(() =>
    toLocalISO(
      source
        ? addDays(parseLocalDate(source.weekStart), source.day + 7)
        : addDays(monday, initialDay),
    ),
  );
  const [carryLinks, setCarryLinks] = useState(true);

  const linkedObservations = sourceLessonId
    ? observations.filter((o) => isLinkedTo(o, sourceLessonId)).length
    : 0;
  const linkedMarks = sourceLessonId
    ? evaluations.filter((e) => isLinkedTo(e, sourceLessonId)).length
    : 0;
  const hasLinks = linkedObservations + linkedMarks > 0;

  const isRitual = slot === -1;
  const isOtherSubject = subject === 'Autre';
  // The colour preview is derived, not stored in a parallel piece of state as
  // the original had to do with updateLessonColorPreview().
  const previewColor = isRitual ? RITUAL_COLOR : subjectColor(subject);
  const previewFamily = isRitual ? 'Rituels' : subjectFamily(subject);

  function formInput(onDay: DayIndex): LessonInput {
    return {
      day: onDay,
      slot,
      subject,
      domain: isOtherSubject ? '' : domain,
      title,
      detail,
      lessonNumber: isOtherSubject ? '' : lessonNumber,
      competenceIds,
      material,
      differentiation,
    };
  }

  function save() {
    if (sourceLessonId) {
      saveCopy(sourceLessonId);
      return;
    }

    const closure = closureForDate(addDays(monday, day));
    if (closure) {
      toast(`${DAYS[day]} : ${closure.label}`);
      return;
    }

    const occupied = useStore.getState().hasLessonAt(day, slot, lessonId);
    if (occupied && !confirm('Ce créneau est déjà occupé. Le remplacer ?')) return;

    useStore.getState().saveLesson(lessonId, formInput(day));
    closeModal();
    toast('Semainier mis à jour');
  }

  function saveCopy(fromId: string) {
    const position = schoolDayPositionISO(targetDate);
    if (!position || closureForISO(targetDate)) {
      toast(targetLabel(targetDate));
      return;
    }

    const store = useStore.getState();
    const occupied = store.hasLessonAt(position.day, slot, null, position.weekStart);
    if (occupied && !confirm('Ce créneau est déjà occupé. Le remplacer ?')) return;

    const copyId = store.duplicateLesson(
      fromId,
      position.weekStart,
      formInput(position.day),
      carryLinks && hasLinks,
    );
    if (!copyId) {
      toast('Cette activité ne peut pas être dupliquée');
      return;
    }

    closeModal();
    toast(`Activité dupliquée : ${targetLabel(targetDate)}`);
  }

  function remove() {
    if (!lessonId) return;
    useStore.getState().deleteLesson(lessonId);
    closeModal();
    toast('Activité supprimée');
  }

  function duplicate() {
    if (!lessonId || !existing) return;
    openModal(
      'Dupliquer l’activité',
      <LessonModal
        lessonId={null}
        sourceLessonId={lessonId}
        initialDay={existing.day}
        initialSlot={existing.slot}
      />,
    );
  }

  return (
    <>
      <div class="formGrid">
        {sourceLessonId ? (
          <div>
            <label for="ldate">Date</label>
            <input
              id="ldate"
              type="date"
              value={targetDate}
              min={SCHOOL_YEAR_START}
              max={SCHOOL_YEAR_END}
              onInput={(e) => setTargetDate(e.currentTarget.value)}
            />
          </div>
        ) : (
          <div>
            <label for="lday">Jour</label>
            <select
              id="lday"
              value={String(day)}
              onChange={(e) => setDay(Number(e.currentTarget.value) as DayIndex)}
            >
              {DAYS.map((name, i) => {
                const closure = closureForDate(addDays(monday, i));
                return (
                  <option key={name} value={String(i)} disabled={closure !== null}>
                    {name} {frDayDate(addDays(monday, i))}
                    {closure ? ` — ${closure.label}` : ''}
                  </option>
                );
              })}
            </select>
          </div>
        )}

        {sourceLessonId && (
          <div>
            <label for="lslot">Créneau</label>
            <select
              id="lslot"
              value={String(slot)}
              onChange={(e) => setSlot(Number(e.currentTarget.value) as SlotIndex)}
            >
              <option value="-1">{RITUAL_TITLE} ({RITUAL_START} – {RITUAL_END})</option>
              {PERIODS.map((period) => (
                <option key={period.slot} value={String(period.slot)}>
                  {period.start} – {period.end}
                </option>
              ))}
            </select>
          </div>
        )}

        {sourceLessonId && (
          <div class="small muted" style={{ gridColumn: '1/-1' }}>
            <b>{targetLabel(targetDate)}</b>
            <br />
            Choisis la date et le créneau de la nouvelle carte, dans n’importe quelle semaine de
            l’année. Son contenu peut être adapté avant de l’enregistrer.
          </div>
        )}

        {sourceLessonId && (
          <label
            style={{
              gridColumn: '1/-1',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              margin: 0,
            }}
          >
            <input
              id="lcarry"
              type="checkbox"
              style={{ flex: '0 0 auto', width: '20px', height: '20px' }}
              checked={hasLinks && carryLinks}
              disabled={!hasLinks}
              onChange={(e) => setCarryLinks(e.currentTarget.checked)}
            />
            <span>
              {hasLinks
                ? `Lier aussi la copie à ce qui a été noté pendant la leçon d’origine : ${count(
                    linkedObservations,
                    'observation',
                    'observations',
                  )} et ${count(linkedMarks, 'résultat d’évaluation', 'résultats d’évaluation')}`
                : 'Aucune observation ni évaluation n’est liée à la leçon d’origine.'}
            </span>
          </label>
        )}

        <div style={{ gridColumn: '1/-1' }}>
          <label for="lsub">Matière</label>
          <select id="lsub" value={subject} onChange={(e) => {
            const nextSubject = e.currentTarget.value;
            setSubject(nextSubject);
            setDomain('');
            if (nextSubject === 'Autre') setLessonNumber('');
          }}>
            {SUBJECTS.map(([name]) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </div>

        {subjectDomains(subject).length > 0 && (
          <div style={{ gridColumn: '1/-1' }}>
            <label for="ldomain">Domaine</label>
            <select id="ldomain" value={domain} onChange={(e) => setDomain(e.currentTarget.value)}>
              <option value="">Choisir un domaine</option>
              {subjectDomains(subject).map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </div>
        )}

        <div style={{ gridColumn: '1/-1' }}>
          <label>Couleur du semainier</label>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '11px',
              padding: '12px 14px',
              border: '1px solid #e5e7eb',
              borderRadius: '14px',
              background: '#fafafa',
            }}
          >
            <span
              style={{
                width: '30px',
                height: '30px',
                borderRadius: '10px',
                background: previewColor,
                border: '1px solid rgba(0,0,0,.06)',
              }}
            />
            <div>
              <b>{previewFamily}</b>
              <div class="small muted">Attribuée automatiquement</div>
            </div>
          </div>
        </div>

        {!isOtherSubject && (
          <div style={{ gridColumn: '1/-1' }}>
            <label for="lnumber">N° de leçon / référence</label>
            <input
              id="lnumber"
              value={lessonNumber}
              placeholder="Ex. 12 ou FR-12"
              onInput={(e) => setLessonNumber(e.currentTarget.value)}
            />
            <div class="small muted" style={{ marginTop: '5px' }}>
              Ce numéro apparaîtra dans la liste des leçons pour retrouver ta préparation dans tes fardes.
            </div>
          </div>
        )}

        <div style={{ gridColumn: '1/-1' }}>
          <label for="ltitle">Titre</label>
          <input
            id="ltitle"
            value={title}
            placeholder="Ex. Numération"
            lang="fr"
            spellcheck={true}
            autocorrect="on"
            autocapitalize="sentences"
            aria-describedby="lesson-spelling-hint"
            onInput={(e) => setTitle(e.currentTarget.value)}
          />
        </div>

        <div style={{ gridColumn: '1/-1' }}>
          <label for="ldetail">Détail / objectif</label>
          <textarea
            id="ldetail"
            value={detail}
            lang="fr"
            spellcheck={true}
            autocorrect="on"
            autocapitalize="sentences"
            aria-describedby="lesson-spelling-hint"
            onInput={(e) => setDetail(e.currentTarget.value)}
          />
        </div>

        <div style={{ gridColumn: '1/-1' }}>
          <CompetenceField
            subject={subject}
            slot={slot}
            value={competenceIds}
            onChange={setCompetenceIds}
          />
        </div>

        <div>
          <label for="lmaterial">Matériel</label>
          <input
            id="lmaterial"
            value={material}
            placeholder="Ex. abaques, fiches"
            lang="fr"
            spellcheck={true}
            autocorrect="on"
            autocapitalize="sentences"
            aria-describedby="lesson-spelling-hint"
            onInput={(e) => setMaterial(e.currentTarget.value)}
          />
        </div>

        <div style={{ gridColumn: '1/-1' }}>
          <label for="ldiff">Différenciation / élèves</label>
          <input
            id="ldiff"
            value={differentiation}
            placeholder="Facultatif"
            lang="fr"
            spellcheck={true}
            autocorrect="on"
            autocapitalize="sentences"
            aria-describedby="lesson-spelling-hint"
            onInput={(e) => setDifferentiation(e.currentTarget.value)}
          />
        </div>

        <p id="lesson-spelling-hint" class="spellingHint" style={{ gridColumn: '1/-1' }}>
          Les mots mal orthographiés sont soulignés. Appuie sur un mot (ou fais un clic droit)
          pour afficher les corrections proposées.
        </p>
      </div>

      <div class="row" style={{ marginTop: '14px' }}>
        <button class="btn primary" onClick={save}>
          Enregistrer
        </button>
        {lessonId && (
          <button class="btn" onClick={duplicate}>
            ⧉ Dupliquer
          </button>
        )}
        {lessonId && (
          <button class="btn danger" onClick={remove}>
            Supprimer
          </button>
        )}
      </div>
    </>
  );
}
